import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, X, ShieldCheck, Search, RotateCcw, Pencil, ArrowLeftRight, Trash2 } from 'lucide-react';
import { moduleApi } from '../../../lib/api';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { useConfirm } from '../../ui/ConfirmProvider';

interface BuiltinGroup { canonical: string; synonyms: string[]; members: string[]; kind?: string; own?: boolean; group_id?: string; }
interface CustomTerm { id: string; family: string; term: string; }

/** Écran « Allergènes & restrictions » (bouton depuis Menu & Plats).
 *
 *  Deux listes, même mécanique : ce sont elles qui décident si un plat déclenche une alerte.
 *   - Les 14 ALLERGÈNES réglementaires (INCO) : font qu'un convive déclarant "arachide" est alerté par un
 *     ingrédient écrit "cacahuète".
 *   - Les RESTRICTIONS non-allergènes (Halal, Végétarien…) : font qu'un plat au vin blanc est signalé à un
 *     convive halal. Ces mots-clés vivaient en dur dans la page convive, invisibles et non modifiables —
 *     Emeni demandait à juste titre "comment le système sait que le vin blanc c'est de l'alcool ?" et voulait
 *     pouvoir déclarer le rhum à son tour.
 *
 *  Tout est complétable, et un terme intégré peut être retiré : le retrait est enregistré comme une
 *  désactivation réversible, l'entrée reste affichée barrée et se remet d'un clic, pour qu'un allergène
 *  retiré par erreur ne disparaisse jamais sans laisser de trace. */
export function AllergenFamiliesPage() {
    const navigate = useNavigate();
    const confirmAction = useConfirm();
    const [allergens, setAllergens] = React.useState<BuiltinGroup[]>([]);
    const [restrictions, setRestrictions] = React.useState<BuiltinGroup[]>([]);
    const [custom, setCustom] = React.useState<CustomTerm[]>([]);
    const [disabled, setDisabled] = React.useState<CustomTerm[]>([]);
    const [labels, setLabels] = React.useState<CustomTerm[]>([]);
    const [allergenRefs, setAllergenRefs] = React.useState<CustomTerm[]>([]);
    const [removedGroups, setRemovedGroups] = React.useState<{ id: string; family: string; kind: string }[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [search, setSearch] = React.useState('');
    const [draft, setDraft] = React.useState<Record<string, string>>({});
    const [saving, setSaving] = React.useState('');
    // Deux onglets plutôt que deux sections empilées : 14 allergènes suivis de 7 restrictions faisaient une
    // page très longue où l'on ne savait plus laquelle des deux listes on était en train de modifier.
    const [tab, setTab] = React.useState<'allergenes' | 'restrictions'>('allergenes');
    // Renommage et création passent par une vraie modale : window.prompt ne se met pas au thème de
    // l'application, n'explique rien et n'est pas utilisable au clavier de la même façon. Retour d'Emeni
    // (2026-08-23) : "quand je clique sur le crayon ça doit ouvrir une modale".
    const [rename, setRename] = React.useState<{ canonical: string; value: string } | null>(null);
    const [creating, setCreating] = React.useState<string | null>(null);
    const [editing, setEditing] = React.useState<{ g: BuiltinGroup; kind: 'allergene' | 'restriction' } | null>(null);

    const load = React.useCallback(() => {
        setLoading(true);
        moduleApi.getAllergenTerms()
            .then((d: any) => {
                setAllergens(d?.builtin || []);
                setRestrictions(d?.restrictions || []);
                setCustom(d?.custom || []);
                setDisabled(d?.disabled || []);
                setLabels(d?.labels || []);
                setAllergenRefs(d?.allergen_refs || []);
                setRemovedGroups(d?.removed_groups || []);
            })
            .catch((e: any) => setError(e?.message || 'Erreur de chargement.'))
            .finally(() => setLoading(false));
    }, []);
    React.useEffect(() => { load(); }, [load]);

    const addTerm = async (group: string) => {
        const term = (draft[group] || '').trim();
        if (!term) return;
        setSaving(group);
        setError('');
        try {
            await moduleApi.addAllergenTerm({ family: group, term });
            setDraft((p) => ({ ...p, [group]: '' }));
            load();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'ajout.");
        } finally {
            setSaving('');
        }
    };

    const removeTerm = async (t: CustomTerm) => {
        setError('');
        try {
            await moduleApi.deleteAllergenTerm(t.id);
            setCustom((p) => p.filter((x) => x.id !== t.id));
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la suppression.');
        }
    };

    // Retirer un terme INTÉGRÉ : désactivation réversible, jamais une suppression définitive.
    const disableBuiltin = async (group: string, term: string, kind: 'allergene' | 'restriction') => {
        const conseq = kind === 'allergene'
            ? 'Les convives allergiques ne seront plus alertés par cet ingrédient.'
            : 'Les plats contenant ce mot ne seront plus signalés pour cette restriction.';
        if (!(await confirmAction({ title: 'Retirer cet élément ?', description: <>« {term} » sera retiré de « {group} ». {conseq} Vous pourrez le remettre depuis la liste des éléments retirés, juste en dessous.</>, confirmLabel: 'Retirer l\'élément', destructive: false }))) return;
        setError('');
        try {
            await moduleApi.addAllergenTerm({ family: group, term, disabled: true });
            load();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors du retrait.');
        }
    };

    const restoreBuiltin = async (t: CustomTerm) => {
        setError('');
        try {
            await moduleApi.deleteAllergenTerm(t.id);
            setDisabled((p) => p.filter((x) => x.id !== t.id));
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la remise.');
        }
    };

    // Renommer une famille ne change QUE son affichage : le nom d'origine reste un synonyme reconnu, et le
    // nouveau nom couvre lui aussi toute la famille (kind 'label'). Un convive qui écrit l'un ou l'autre est
    // détecté de la même façon. Demande d'Emeni (2026-08-23) : "le nom Poisson doit être modifiable".
    const submitRename = async () => {
        if (!rename) return;
        const value = rename.value.trim();
        if (!value) return;
        setError('');
        try {
            await moduleApi.addAllergenTerm({ family: rename.canonical, term: value, kind: 'label' });
            setRename(null);
            load();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors du renommage.');
        }
    };

    // Retirer un GROUPE entier : plus aucun de ses ingrédients ne déclenche d'alerte, et il disparaît du
    // formulaire du convive s'il s'agit d'une restriction. Enregistré comme une désactivation réversible,
    // jamais comme une suppression sèche — un allergène retiré par erreur doit pouvoir être remis, et rester
    // visible en attendant. Demande d'Emeni (2026-08-23).
    const removeGroup = async (g: BuiltinGroup, kind: 'allergene' | 'restriction') => {
        const nom = labels.find((l) => l.family === g.canonical)?.term || g.canonical;
        const conseq = kind === 'allergene'
            ? `Plus AUCUN de ses ingrédients ne déclenchera d'alerte : un convive allergique ne sera plus prévenu sur les plats qui en contiennent.`
            : `Elle disparaîtra du formulaire des convives et ne signalera plus aucun plat.`;
        if (!(await confirmAction({ title: 'Retirer cette famille ?', description: <>« {nom} » sera retirée. {conseq} Vous pourrez la remettre depuis la liste des éléments retirés, en bas de page.</>, confirmLabel: 'Retirer la famille', destructive: false }))) return;
        setError('');
        try {
            await moduleApi.addAllergenTerm({ family: g.canonical, term: g.canonical, kind: 'group', disabled: true, group_kind: kind });
            setEditing(null);
            load();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors du retrait.');
        }
    };

    const restoreGroup = async (id: string) => {
        setError('');
        try {
            await moduleApi.deleteAllergenTerm(id);
            load();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la remise.');
        }
    };

    const labelFor = (canonical: string) => labels.find((l) => l.family === canonical)?.term || canonical;

    // Une restriction se compose d'ALLERGÈNES cochés, pas d'une liste de mots recopiés : "Vegan" = Œuf +
    // Lactose + Viande + Poisson. L'information reste à un seul endroit, et enrichir un allergène profite
    // du même coup à toutes les restrictions qui le référencent. Idée d'Emeni (2026-08-23).
    const refsOf = (restriction: string) => allergenRefs.filter((r) => r.family === restriction).map((r) => r.term);
    const toggleAllergenRef = async (restriction: string, allergen: string) => {
        const existing = allergenRefs.find((r) => r.family === restriction && r.term === allergen);
        setError('');
        try {
            if (existing) await moduleApi.deleteAllergenTerm(existing.id);
            else await moduleApi.addAllergenTerm({ family: restriction, term: allergen, kind: 'allergen_ref' });
            load();
        } catch (e: any) {
            setError(e?.message || 'Erreur.');
        }
    };

    // Créer un groupe de toutes pièces (« Sans épices », « Sans oignon »…) : il se comporte comme les autres,
    // une liste de mots cherchés dans les ingrédients. Rangé côté restrictions, il devient une case à cocher
    // dans le formulaire du convive. Demande d'Emeni (2026-08-23).
    const submitCreate = async () => {
        const value = (creating || '').trim();
        if (!value) return;
        setError('');
        try {
            await moduleApi.addAllergenTerm({ family: value, term: value, kind: 'group', group_kind: tab === 'allergenes' ? 'allergene' : 'restriction' });
            setCreating(null);
            load();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la création.');
        }
    };

    // Déplacer un groupe d'un onglet à l'autre. Ce qui change vraiment : une restriction est proposée en
    // case à cocher au convive, un allergène est déclaré via ses ingrédients. La détection, elle, reste la
    // même — un « Lactose » déplacé garde tous ses ingrédients comme mots-clés.
    const moveGroup = async (g: BuiltinGroup) => {
        const to = tab === 'allergenes' ? 'restriction' : 'allergene';
        const msg = to === 'restriction'
            ? `Déplacer « ${labelFor(g.canonical)} » vers les restrictions ?

Le convive pourra le cocher directement dans son formulaire.`
            : `Déplacer « ${labelFor(g.canonical)} » vers les allergènes ?

Il ne sera plus proposé en case à cocher : le convive le déclarera via ses ingrédients.`;
        if (!(await confirmAction({ title: 'Changer le type de cette famille ?', description: <span className="whitespace-pre-line">{msg}</span>, confirmLabel: 'Changer le type', destructive: false }))) return;
        setError('');
        try {
            await moduleApi.addAllergenTerm({ family: g.canonical, term: labelFor(g.canonical), kind: 'label', group_kind: to });
            load();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors du déplacement.');
        }
    };

    const q = search.trim().toLowerCase();
    const matches = (g: BuiltinGroup) => !q
        || g.canonical.toLowerCase().includes(q)
        || labelFor(g.canonical).toLowerCase().includes(q)
        || g.synonyms.some((s) => s.toLowerCase().includes(q))
        || g.members.some((m) => m.toLowerCase().includes(q))
        || custom.some((c) => c.family === g.canonical && c.term.toLowerCase().includes(q));

    /** Carte compacte de la liste : le nom, un aperçu, et le nombre d'éléments. Toute l'édition se fait dans
     *  la modale — avec 21 groupes de 10 à 27 ingrédients chacun, tout déplier d'un coup rendait la page
     *  illisible et l'on ne savait plus quel groupe on modifiait. Retour d'Emeni (2026-08-23) : "ça doit
     *  ouvrir une modale complète comme pour les plats, pas juste le titre". */
    const renderCard = (g: BuiltinGroup, kind: 'allergene' | 'restriction') => {
        const mine = custom.filter((c) => c.family === g.canonical);
        const off = new Set(disabled.filter((c) => c.family === g.canonical).map((c) => c.term.toLowerCase()));
        const actifs = g.members.filter((m) => !off.has(m.toLowerCase()));
        const refs = kind === 'restriction' ? refsOf(g.canonical) : [];
        const apercu = kind === 'restriction' && refs.length ? refs : [...actifs, ...mine.map((m) => m.term)];
        return (
            <button key={g.canonical} type="button" onClick={() => setEditing({ g, kind })}
                className="w-full text-left bg-surface-soft border border-border rounded-xl p-3 hover:border-brand-500/50 transition-colors">
                <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-bold text-content">{labelFor(g.canonical)}</span>
                    <span className="text-[10px] text-content-muted shrink-0">
                        {kind === 'restriction' && refs.length ? `${refs.length} allergène(s)` : `${actifs.length + mine.length} ingrédient(s)`}
                    </span>
                </div>
                <p className="text-[10px] text-content-muted mt-0.5 truncate">
                    {apercu.length ? apercu.slice(0, 8).join(', ') + (apercu.length > 8 ? '…' : '') : 'Rien de configuré — clique pour compléter.'}
                </p>
            </button>
        );
    };

    const renderGroup = (g: BuiltinGroup, kind: 'allergene' | 'restriction') => {
        const mine = custom.filter((c) => c.family === g.canonical);
        const off = disabled.filter((c) => c.family === g.canonical);
        const offKeys = new Set(off.map((c) => c.term.toLowerCase()));
        const actifs = g.members.filter((m) => !offKeys.has(m.toLowerCase()));
        const placeholder = kind === 'allergene'
            ? `Ajouter un ingrédient contenant « ${labelFor(g.canonical).toLowerCase()} »`
            : `Mot supplémentaire, si aucun allergène ne convient (optionnel)`;

        return (
            <div key={g.canonical} className="space-y-2">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <h3 className="text-sm font-bold text-content flex items-center gap-1.5">
                        {labelFor(g.canonical)}
                        <button type="button" onClick={() => setRename({ canonical: g.canonical, value: labelFor(g.canonical) })}
                            className="p-0.5 rounded hover:bg-surface text-content-muted hover:text-content" title="Renommer">
                            <Pencil size={11} />
                        </button>
                        <button type="button" onClick={() => moveGroup(g)}
                            className="p-0.5 rounded hover:bg-surface text-content-muted hover:text-content" title={kind === 'allergene' ? 'Déplacer vers les restrictions' : 'Déplacer vers les allergènes'}>
                            <ArrowLeftRight size={11} />
                        </button>
                        {labelFor(g.canonical) !== g.canonical && (
                            <span className="text-[9px] font-normal text-content-muted">(anciennement {g.canonical})</span>
                        )}
                    </h3>
                    {g.synonyms.length > 0 && (
                        <p className="text-[10px] text-content-muted">aussi écrit : {g.synonyms.join(', ')}</p>
                    )}
                </div>

                <div className="flex flex-wrap gap-1">
                    {actifs.map((m) => (
                        <span key={m} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-surface border border-border rounded-full pl-2.5 pr-1 py-0.5 text-content-muted">
                            {m}
                            <button type="button" onClick={() => disableBuiltin(g.canonical, m, kind)}
                                className="p-0.5 rounded-full hover:bg-red-500/10 text-content-muted hover:text-red-400" title={`Retirer « ${m} »`}>
                                <X size={10} />
                            </button>
                        </span>
                    ))}
                    {actifs.length === 0 && <span className="text-[10px] text-content-muted italic">Aucun élément actif.</span>}
                </div>

                {mine.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1 border-t border-border">
                        {mine.map((t) => (
                            <span key={t.id} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-brand-500/10 border border-brand-500/30 rounded-full pl-2.5 pr-1 py-0.5 text-brand-600 dark:text-brand-400">
                                {t.term}
                                <button type="button" onClick={() => removeTerm(t)} className="p-0.5 rounded-full hover:bg-red-500/10 text-content-muted hover:text-red-400" title="Retirer">
                                    <X size={10} />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {off.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1 pt-1 border-t border-border">
                        <span className="text-[10px] text-content-muted italic">Retirés :</span>
                        {off.map((t) => (
                            <button key={t.id} type="button" onClick={() => restoreBuiltin(t)}
                                className="inline-flex items-center gap-1 text-[10px] font-semibold bg-surface border border-dashed border-border rounded-full px-2 py-0.5 text-content-muted line-through hover:no-underline hover:text-content hover:border-solid"
                                title="Remettre">
                                {t.term} <RotateCcw size={9} />
                            </button>
                        ))}
                    </div>
                )}

                {kind === 'restriction' && (
                    <div className="space-y-1 pt-1.5 border-t border-border">
                        <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Composée de ces allergènes</p>
                        <p className="text-[10px] text-content-muted">
                            Coche les allergènes concernés plutôt que de retaper leurs ingrédients : « Vegan » = Œuf + Lactose + Viande + Poisson.
                            Enrichir un allergène met à jour toutes les restrictions qui l'utilisent.
                        </p>
                        <div className="flex flex-wrap gap-1">
                            {allAllergenNames.map((a) => {
                                const on = refsOf(g.canonical).includes(a);
                                return (
                                    <button key={a} type="button" onClick={() => toggleAllergenRef(g.canonical, a)}
                                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition-all ${on ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                        {a}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-1.5">
                    <Input value={draft[g.canonical] || ''} onChange={(e) => setDraft({ ...draft, [g.canonical]: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTerm(g.canonical); } }}
                        placeholder={placeholder} className="flex-1" />
                    <Button type="button" size="sm" variant="secondary" onClick={() => addTerm(g.canonical)} disabled={saving === g.canonical || !(draft[g.canonical] || '').trim()}>
                        <Plus size={12} /> {saving === g.canonical ? '...' : 'Ajouter'}
                    </Button>
                </div>
            </div>
        );
    };

    // Tous les allergènes existants, y compris ceux déplacés côté restrictions : ils restent des groupes
    // d'ingrédients utilisables dans une composition.
    const allAllergenNames = React.useMemo(
        () => allergens.map((g) => g.canonical).sort((a, b) => a.localeCompare(b, 'fr')),
        [allergens]);

    const visibleAllergens = allergens.filter(matches);
    const visibleRestrictions = restrictions.filter(matches);
    // Une recherche qui ne trouve rien dans l'onglet courant bascule sur l'autre : sinon taper "halal" depuis
    // l'onglet Allergènes affiche "aucun résultat" alors que la restriction existe juste à côté.
    React.useEffect(() => {
        if (!q) return;
        if (tab === 'allergenes' && visibleAllergens.length === 0 && visibleRestrictions.length > 0) setTab('restrictions');
        if (tab === 'restrictions' && visibleRestrictions.length === 0 && visibleAllergens.length > 0) setTab('allergenes');
    }, [q, tab, visibleAllergens.length, visibleRestrictions.length]);

    return (
        // Élargi comme le catalogue de plats dont elle dépend : ce sont des listes, pas un formulaire.
        <div className="space-y-4">
            {/* Pas de largeur maximale : cette page présente une LISTE, pas un formulaire — elle gagne à occuper l'écran d'un ordinateur. Les écrans de réglages gardent leur plafond : un formulaire étalé sur 1900 px devient illisible. Demande d'Emeni (2026-08-25). */}
            <button onClick={() => navigate('/menu-plats')} className="flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-content">
                <ArrowLeft size={14} /> Menu & Plats
            </button>

            <div>
                <h1 className="text-xl font-bold text-content flex items-center gap-2"><ShieldCheck size={18} /> Allergènes &amp; restrictions</h1>
                <p className="text-xs text-content-muted mt-1">
                    C'est ce qui permet au système de reconnaître un problème sans que tu aies à le déclarer plat par plat :
                    un convive qui écrit « arachide » est alerté par un ingrédient saisi « cacahuète », et un plat au
                    « vin blanc » est signalé à un convive halal.
                </p>
                <p className="text-xs text-content-muted mt-1.5">
                    Complète ces listes avec ton vocabulaire : rattache « sauce curry » au lait, ajoute « rhum » à l'alcool.
                    Tout ce que tu ajoutes ici est reconnu partout — côté convive, sur la fiche cuisine et en mode terrain.
                    Les deux onglets se configurent de la même façon ; seule change la conséquence pour le convive.
                </p>
                <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5 font-semibold">
                    Retirer un élément supprime l'alerte correspondante pour tous tes convives. « Parmesan » retiré du lait,
                    et un convive intolérant au lactose ne sera plus prévenu sur un plat au parmesan.
                </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setTab('allergenes')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === 'allergenes' ? 'bg-brand-600 text-brand-fg' : 'bg-surface-soft text-content-muted hover:text-content'}`}>
                    Allergènes ({allergens.length})
                </button>
                <button onClick={() => setTab('restrictions')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === 'restrictions' ? 'bg-brand-600 text-brand-fg' : 'bg-surface-soft text-content-muted hover:text-content'}`}>
                    Restrictions alimentaires ({restrictions.length})
                </button>
            </div>

            <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un ingrédient, un allergène, une restriction..."
                    className="w-full pl-8 pr-3 py-2 rounded-lg bg-surface-soft border border-border text-sm text-content outline-none focus:border-brand-500" />
            </div>

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={20} /></div>
            ) : (
                <div className="space-y-2.5">
                    {/* La seule différence entre les deux onglets tient au blocage : la mécanique de
                        configuration est identique, et Emeni l'avait bien vu ("les deux fonctionnent
                        quasiment de la même manière"). Autant l'écrire là où il la lit, plutôt que de
                        laisser deviner une différence qui n'existe pas. */}
                    <div className={`rounded-xl border p-2.5 text-[11px] ${tab === 'allergenes' ? 'bg-red-500/5 border-red-500/30' : 'bg-amber-500/5 border-amber-500/30'}`}>
                        {tab === 'allergenes' ? (
                            <p className="text-content">
                                <strong className="text-red-600 dark:text-red-400">Un allergène bloque le convive.</strong> S'il déclare cette allergie
                                et qu'un plat en contient, il ne peut pas valider tant qu'il n'a pas fait retirer l'ingrédient ou changé de plat.
                                <span className="block text-content-muted mt-1">Les 14 allergènes réglementaires et les ingrédients qui en relèvent.</span>
                            </p>
                        ) : (
                            <p className="text-content">
                                <strong className="text-amber-700 dark:text-amber-400">Une restriction avertit seulement.</strong> Le convive est prévenu,
                                mais reste libre de valider son choix — un végétarien qui commande du poisson en connaissance de cause, c'est sa décision.
                                <span className="block text-content-muted mt-1">Ce sont exactement les restrictions proposées dans son formulaire : les renommer change aussi ce qu'il voit.</span>
                            </p>
                        )}
                    </div>
                    <p className="text-[10px] text-content-muted">
                        La configuration est la même des deux côtés : une liste de mots, cherchés dans les ingrédients de chaque plat.
                    </p>
                    <div className="flex justify-end">
                        <Button size="sm" variant="secondary" onClick={() => setCreating('')}>
                            <Plus size={12} /> {tab === 'allergenes' ? 'Ajouter un allergène' : 'Ajouter une restriction'}
                        </Button>
                    </div>
                    {tab === 'allergenes' ? (
                        <>
                            {visibleAllergens.length === 0 && <p className="text-sm text-content-muted">Aucun allergène ne correspond à la recherche.</p>}
                            {visibleAllergens.map((g) => renderCard(g, 'allergene'))}
                        </>
                    ) : (
                        <>
                            {visibleRestrictions.length === 0 && <p className="text-sm text-content-muted">Aucune restriction ne correspond à la recherche.</p>}
                            {visibleRestrictions.map((g) => renderCard(g, 'restriction'))}
                        </>
                    )}
                </div>
            )}

            {removedGroups.length > 0 && !loading && (
                <div className="space-y-1.5 pt-2 border-t border-border">
                    <h2 className="text-[11px] font-black text-content-muted uppercase tracking-widest">Retirés ({removedGroups.length})</h2>
                    <p className="text-[10px] text-content-muted">
                        Ces groupes ne déclenchent plus aucune alerte. Clique pour les remettre.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {removedGroups.map((r) => (
                            <button key={r.id} type="button" onClick={() => restoreGroup(r.id)}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold bg-surface-soft border border-dashed border-border rounded-full px-2.5 py-1 text-content-muted line-through hover:no-underline hover:text-content hover:border-solid">
                                {r.family} <RotateCcw size={10} />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {editing && (() => {
                // Le groupe est relu depuis la liste à chaque rendu : après un ajout ou un retrait, la modale
                // doit refléter l'état rechargé, pas la copie figée au moment du clic.
                const src = (editing.kind === 'allergene' ? allergens : restrictions).find((x) => x.canonical === editing.g.canonical) || editing.g;
                return (
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={() => setEditing(null)}>
                        <div className="bg-surface border border-border w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] sm:max-h-[88vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border shrink-0">
                                <h3 className="text-sm font-bold text-content">
                                    {editing.kind === 'allergene' ? 'Allergène' : 'Restriction'} — {labelFor(src.canonical)}
                                </h3>
                                <button type="button" onClick={() => setEditing(null)} className="p-1 rounded-lg hover:bg-surface-soft text-content-muted hover:text-content"><X size={16} /></button>
                            </div>
                            <div className="overflow-y-auto px-4 py-3.5 flex-1">{renderGroup(src, editing.kind)}</div>
                            <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-border shrink-0">
                                <Button variant="secondary" size="sm" onClick={() => removeGroup(src, editing.kind)}
                                    className="text-red-500! border-red-500/40! hover:bg-red-500/10!">
                                    <Trash2 size={12} /> Retirer
                                </Button>
                                <Button size="sm" onClick={() => setEditing(null)}>Fermer</Button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {rename && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setRename(null)}>
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-sm p-4 space-y-3 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-sm font-bold text-content">Renommer « {rename.canonical} »</h3>
                        <p className="text-[11px] text-content-muted">
                            Seul l'affichage change : le nom d'origine « {rename.canonical} » reste reconnu, et le nouveau nom
                            couvre lui aussi toute la famille. Un convive qui écrit l'un ou l'autre est détecté de la même façon.
                        </p>
                        <Input autoFocus value={rename.value} onChange={(e) => setRename({ ...rename, value: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitRename(); } }} />
                        <div className="flex justify-end gap-2">
                            <Button variant="secondary" size="sm" onClick={() => setRename(null)}>Annuler</Button>
                            <Button size="sm" onClick={submitRename} disabled={!rename.value.trim()}>Renommer</Button>
                        </div>
                    </div>
                </div>
            )}

            {creating !== null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setCreating(null)}>
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-sm p-4 space-y-3 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-sm font-bold text-content">{tab === 'allergenes' ? 'Nouvel allergène' : 'Nouvelle restriction'}</h3>
                        <p className="text-[11px] text-content-muted">
                            {tab === 'allergenes'
                                ? "Un groupe d'ingrédients de plus, à compléter ensuite avec les ingrédients qui en relèvent."
                                : "Elle apparaîtra comme case à cocher dans le formulaire du convive. Compose-la ensuite en cochant les allergènes concernés."}
                        </p>
                        <Input autoFocus value={creating} onChange={(e) => setCreating(e.target.value)}
                            placeholder={tab === 'allergenes' ? 'Ex : Sulfites de mon fournisseur' : 'Ex : Sans épices'}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitCreate(); } }} />
                        <div className="flex justify-end gap-2">
                            <Button variant="secondary" size="sm" onClick={() => setCreating(null)}>Annuler</Button>
                            <Button size="sm" onClick={submitCreate} disabled={!creating.trim()}>Créer</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
